type CheckName =
  | 'camera'
  | 'audioRecording'
  | 'existingAudio'
  | 'documents'
  | 'dragDrop'
  | 'resume'
  | 'permissionDenied'
  | 'backgroundRecovery';

type CheckStatus = 'open' | 'passed' | 'failed' | 'na';

const checkNames: CheckName[] = [
  'camera',
  'audioRecording',
  'existingAudio',
  'documents',
  'dragDrop',
  'resume',
  'permissionDenied',
  'backgroundRecovery',
];

const labels: Record<CheckName, string> = {
  camera: 'Kamera-Foto',
  audioRecording: 'Audioaufnahme',
  existingAudio: 'Bestehende Audiodatei',
  documents: 'Dokument-Upload',
  dragDrop: 'Drag-and-drop',
  resume: 'Upload-Fortsetzung',
  permissionDenied: 'Abgelehnte Medienberechtigung',
  backgroundRecovery: 'Drehung/Hintergrund und Wiederaufnahme',
};

const form = document.querySelector<HTMLFormElement>('#acceptance-form');
const reportElement = document.querySelector<HTMLElement>('#report');
const capabilitiesElement = document.querySelector<HTMLElement>('#capabilities');
const newReportButton = document.querySelector<HTMLButtonElement>('#new-report');
const copyButton = document.querySelector<HTMLButtonElement>('#copy-report');
const downloadButton = document.querySelector<HTMLButtonElement>('#download-report');
const copyStatus = document.querySelector<HTMLElement>('#copy-status');

if (!form || !reportElement || !capabilitiesElement || !newReportButton || !copyButton || !downloadButton || !copyStatus) {
  throw new Error('Die Geräte-Abnahmeseite ist unvollständig.');
}

const acceptanceForm = form;
const reportOutput = reportElement;
const copyFeedback = copyStatus;

const storageKey = 'universal-input-device-acceptance-v1';
type StoredState = Partial<Record<CheckName, CheckStatus | boolean>> & {
  notes?: string;
  reportId?: string;
  startedAt?: string;
};

function readStoredState(): StoredState {
  try {
    return JSON.parse(localStorage.getItem(storageKey) ?? '{}') as StoredState;
  } catch {
    localStorage.removeItem(storageKey);
    return {};
  }
}

const restoredState = readStoredState();
let reportId = restoredState.reportId ?? createReportId();
let startedAt = restoredState.startedAt ?? new Date().toISOString();
const supportsMedia = Boolean(navigator.mediaDevices?.getUserMedia);
const supportsClipboard = Boolean(navigator.clipboard?.writeText);
const isTouchOnlyDevice = (navigator.maxTouchPoints ?? 0) > 0
  && window.matchMedia('(hover: none) and (pointer: coarse)').matches;

const capabilityRows: Array<[string, string]> = [
  ['Sicherer HTTPS-Kontext', window.isSecureContext ? 'Ja' : 'Nein'],
  ['Kamera/Mikrofon-API', supportsMedia ? 'Verfügbar' : 'Nicht verfügbar'],
  ['Touchpunkte', String(navigator.maxTouchPoints ?? 0)],
  ['Bildschirm', `${window.screen.width} × ${window.screen.height}`],
  ['Browser-Sprache', navigator.language],
  ['Browserkennung', navigator.userAgent],
];

for (const [term, description] of capabilityRows) {
  const dt = document.createElement('dt');
  const dd = document.createElement('dd');
  dt.textContent = term;
  dd.textContent = description;
  capabilitiesElement.append(dt, dd);
}

function restore(): void {
  for (const name of checkNames) {
    const input = acceptanceForm.elements.namedItem(name);
    if (input instanceof HTMLSelectElement) {
      const stored = restoredState[name];
      const defaultStatus = name === 'dragDrop' && isTouchOnlyDevice ? 'na' : 'open';
      input.value = stored === true ? 'passed' : stored === false ? 'open' : normalizeStatus(stored, defaultStatus);
    }
  }
  const notes = acceptanceForm.elements.namedItem('notes');
  if (notes instanceof HTMLTextAreaElement && typeof restoredState.notes === 'string') notes.value = restoredState.notes;
}

function createReportId(): string {
  return crypto.randomUUID?.() ?? `device-${Date.now()}`;
}

function normalizeStatus(value: unknown, fallback: CheckStatus = 'open'): CheckStatus {
  return value === 'passed' || value === 'failed' || value === 'na' ? value : fallback;
}

function currentState(): Record<CheckName | 'notes', CheckStatus | string> {
  const state = {} as Record<CheckName | 'notes', CheckStatus | string>;
  for (const name of checkNames) {
    const input = acceptanceForm.elements.namedItem(name);
    state[name] = input instanceof HTMLSelectElement ? normalizeStatus(input.value) : 'open';
  }
  const notes = acceptanceForm.elements.namedItem('notes');
  state.notes = notes instanceof HTMLTextAreaElement ? notes.value.trim() : '';
  return state;
}

function renderReport(): void {
  const state = currentState();
  localStorage.setItem(storageKey, JSON.stringify({ ...state, reportId, startedAt }));
  const passed = checkNames.filter((name) => state[name] === 'passed').length;
  const failed = checkNames.filter((name) => state[name] === 'failed').length;
  const notApplicable = checkNames.filter((name) => state[name] === 'na').length;
  const open = checkNames.length - passed - failed - notApplicable;
  const applicable = checkNames.length - notApplicable;
  const markers: Record<CheckStatus, string> = { open: ' ', passed: 'x', failed: '!', na: '-' };
  const lines = [
    'Universal Input – Geräte-Abnahme',
    `Berichts-ID: ${reportId}`,
    `Beginn: ${startedAt}`,
    `Aktualisiert: ${new Date().toISOString()}`,
    `Seite: ${window.location.href}`,
    `Gerät/Browser: ${navigator.userAgent}`,
    `HTTPS: ${window.isSecureContext ? 'JA' : 'NEIN'}`,
    `MediaDevices: ${supportsMedia ? 'JA' : 'NEIN'}`,
    `Ergebnis: ${passed}/${applicable} anwendbare Prüfungen erfolgreich; ${failed} fehlgeschlagen; ${open} offen; ${notApplicable} nicht anwendbar`,
    '',
    ...checkNames.map((name) => `[${markers[normalizeStatus(state[name])]}] ${labels[name]}`),
    '',
    `Notizen: ${String(state.notes) || '–'}`,
  ];
  reportOutput.textContent = lines.join('\n');
}

async function copyReport(): Promise<void> {
  const report = reportOutput.textContent ?? '';
  try {
    if (supportsClipboard) {
      await navigator.clipboard.writeText(report);
    } else {
      const textarea = document.createElement('textarea');
      textarea.value = report;
      textarea.style.position = 'fixed';
      textarea.style.opacity = '0';
      document.body.append(textarea);
      textarea.select();
      document.execCommand('copy');
      textarea.remove();
    }
    copyFeedback.textContent = 'Bericht wurde in die Zwischenablage kopiert.';
  } catch {
    copyFeedback.textContent = 'Kopieren war nicht möglich. Bitte den Bericht manuell markieren.';
  }
}

function downloadReport(): void {
  const report = reportOutput.textContent ?? '';
  const blob = new Blob([`${report}\n`], { type: 'text/plain;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = `universal-input-geraeteabnahme-${reportId}.txt`;
  document.body.append(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);
  copyFeedback.textContent = 'Bericht wurde als Textdatei bereitgestellt.';
}

function startNewReport(): void {
  localStorage.removeItem(storageKey);
  reportId = createReportId();
  startedAt = new Date().toISOString();
  for (const name of checkNames) {
    const input = acceptanceForm.elements.namedItem(name);
    if (input instanceof HTMLSelectElement) {
      input.value = name === 'dragDrop' && isTouchOnlyDevice ? 'na' : 'open';
    }
  }
  const notes = acceptanceForm.elements.namedItem('notes');
  if (notes instanceof HTMLTextAreaElement) notes.value = '';
  renderReport();
  copyFeedback.textContent = 'Ein neuer, leerer Gerätebericht wurde gestartet.';
}

restore();
renderReport();
acceptanceForm.addEventListener('input', renderReport);
newReportButton.addEventListener('click', startNewReport);
copyButton.addEventListener('click', copyReport);
downloadButton.addEventListener('click', downloadReport);
